/**
 * The progress rail.
 *
 * Three steps, all three visible from the first screen, so a buyer knows how
 * long this is before they start rather than after. That is the whole reason
 * Adobe's checkout feels short: it is not short, it is FINITE and it says so.
 *
 * ── IT IS A NAVIGATION LANDMARK, NOT A DECORATION ─────────────────────────
 * A completed step is a real button and going back to it does not lose
 * anything, because the page holds one state object and the steps only choose
 * which part of it is on screen. A step the buyer has not reached yet is
 * `disabled`, not hidden: a disabled control still tells them it exists.
 *
 * `aria-current="step"` marks the one they are on. The visual current state is
 * a crimson dot AND a weight change AND the label colour, so it does not rely
 * on colour alone.
 */

import { cn } from "~/app/lib/cn";
import type { StepDescriptor, StepId } from "~/app/components/checkout/checkout-model";
import { stepIndex } from "~/app/components/checkout/checkout-model";

export interface StepRailProps {
  steps: readonly StepDescriptor[];
  current: StepId;
  /** The furthest step the buyer has legitimately reached. */
  furthest: StepId;
  onGo: (step: StepId) => void;
  className?: string;
}

export function StepRail({ steps, current, furthest, onGo, className }: StepRailProps) {
  const at = stepIndex(current);
  const reached = stepIndex(furthest);

  return (
    <nav className={cn("eg-steps-rail no-print", className)} aria-label="Checkout progress">
      <ol>
        {steps.map((step, i) => {
          const state = i < at ? "done" : i === at ? "here" : "todo";
          const reachable = i <= reached;
          return (
            <li
              key={step.id}
              className={cn("eg-steprail-item", `is-${state}`)}
              aria-current={state === "here" ? "step" : undefined}
            >
              <button
                type="button"
                className="eg-steprail-btn"
                disabled={!reachable || state === "here"}
                onClick={() => onGo(step.id)}
              >
                <span className="eg-steprail-mark" aria-hidden="true">
                  {state === "done" ? (
                    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2.5 6.2 4.8 8.6 9.5 3.4" />
                    </svg>
                  ) : (
                    <span className="eg-steprail-n">{i + 1}</span>
                  )}
                </span>
                <span className="eg-steprail-text">
                  <span className="eg-steprail-label">
                    {step.label}
                    <span className="e-sr-only">
                      {state === "done"
                        ? ", completed. Go back to it."
                        : state === "here"
                          ? ", the step you are on"
                          : ", not reached yet"}
                    </span>
                  </span>
                  <span className="eg-steprail-hint">{step.hint}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
